import { Feature } from "ol";
import { Geometry } from "ol/geom";
import GeoJSON from "ol/format/GeoJSON";
import { Fill, Stroke, Style } from "ol/style";

import {
  DeviceStatisticsResponse,
} from "@src/services/devicesService";
import type { TFieldTrackDevice } from "@src/services/fieldsService";
import { GEOJSON_OPTS } from "@screens/FieldsScreen/fieldLayers";
import type { TTrackTimeRender } from "@screens/FieldsScreen/fieldTrackTime";
import { localDayKey } from "@screens/FieldsScreen/fieldTracksCache";
import {
  moistureFeatures,
  notPotatoFeatures,
  potatoFeatures,
  sensorFeatures,
  sizeFeatures,
  speedFeatures,
} from "@screens/TracksScreen/utils";
import {
  sensorValueColor,
  trackKeepFor,
  type TTrackFilter,
} from "@screens/TracksScreen/trackFilter";

// The ramp and the color/speed filter live in TracksScreen/trackFilter so the
// Tracks screen (Mapbox) and this layer (OpenLayers) share one definition.
export {
  SENSOR_BANDS,
  isTrackFilterActive,
  sensorBandColor,
  sensorValueColor,
  type TTrackFilter,
} from "@screens/TracksScreen/trackFilter";

/**
 * Turns the field-tracks payload into OpenLayers features.
 *
 * The geometry builders in TracksScreen/utils are map-library agnostic — they
 * emit plain GeoJSON with color-bearing properties — so the swaths here are
 * built by exactly the same code that draws the Tracks screen. Only the
 * styling differs: Mapbox resolves colors from paint expressions, OpenLayers
 * needs a style per feature, which is what `styleFeature` does.
 */

export type TTrackMetric =
  | "sensor"
  | "speed"
  | "potato"
  | "not_potato"
  | "size"
  | "moisture";

export type TTrackColorMode = "value" | "device" | "date";

/** Metrics that exist only when the machines carry the hardware for them. */
export const CAMERA_METRICS: TTrackMetric[] = ["potato", "not_potato", "size"];

/**
 * Whether the loaded points can serve a metric at all.
 *
 * Both panes ask this, and so does each pane's VM when it decides whether the
 * selected metric survives a change of field or season — one definition so a
 * metric can never be offered by one and drawn blank by the other.
 *
 * Camera data is present-or-absent (`null` = no camera). Moisture is not: ingest
 * writes a literal 0 for a machine whose sensor is absent, so only readings
 * above zero count as a real measurement.
 */
export const tracksHaveCamera = (devices: TFieldTrackDevice[]): boolean =>
  devices.some((d) => d.tracks?.some((t: any) => t.cvPotatoCount != null));

export const tracksHaveMoisture = (devices: TFieldTrackDevice[]): boolean =>
  devices.some((d) => d.tracks?.some((t: any) => (t.moisturePercent ?? 0) > 0));

/**
 * The metric list both track panes offer, in one order.
 *
 * The main panel and the compare pane are the same feature seen twice, so the
 * choices — and the conditions under which each appears — are built here rather
 * than transcribed into each panel.
 */
export function trackMetricOptions(
  devices: TFieldTrackDevice[],
  cameraInstalled: boolean,
  lang: any
): Array<{ title: string; value: TTrackMetric }> {
  const f = lang?.fields ?? {};
  const options: Array<{ title: string; value: TTrackMetric }> = [
    { title: f.tracksMetricSensor ?? "Sensor", value: "sensor" },
    { title: f.tracksMetricSpeed ?? "Speed", value: "speed" },
  ];

  // Camera metrics need the account flag AND per-point data, matching the gate
  // on the Tracks screen.
  if (cameraInstalled && tracksHaveCamera(devices)) {
    options.push(
      { title: f.tracksMetricPotato ?? "Potatoes", value: "potato" },
      { title: f.tracksMetricNotPotato ?? "Not potatoes", value: "not_potato" },
      { title: f.tracksMetricSize ?? "Average size", value: "size" }
    );
  }
  // Grain moisture needs no account flag, only a machine that measured it.
  if (tracksHaveMoisture(devices)) {
    options.push({
      title: f.tracksMetricMoisture ?? "Grain moisture",
      value: "moisture",
    });
  }

  return options;
}

/**
 * The metric to draw with, given what the loaded machines can actually serve.
 *
 * Moving to a field (or season) worked by different machines can take the
 * selected metric away — only grain harvesters measure moisture, only some rigs
 * carry the camera. The panel then stops offering it, and a Dropdown cannot
 * deselect itself, so the pane would sit on an empty map with no way back.
 */
export function resolveTrackMetric(
  metric: TTrackMetric,
  devices: TFieldTrackDevice[]
): TTrackMetric {
  if (metric === "moisture" && !tracksHaveMoisture(devices)) return "sensor";
  if (CAMERA_METRICS.includes(metric) && !tracksHaveCamera(devices)) return "sensor";
  return metric;
}

/**
 * Colour for a date's position in the harvest sequence: red for the first day
 * worked through to green for the last.
 *
 * Dates are ordinal, so a sequential ramp reads far better than the qualitative
 * palette machines use — you can see the field fill in over time at a glance.
 * It reuses the sensor/yield ramp above so red-to-green means the same thing
 * everywhere in the app, stopping at 90 to avoid the dark-grey terminator that
 * caps the sensor scale.
 *
 * A single worked day has no sequence to express, so it takes the green end.
 */
export function dateSequenceColor(index: number, total: number): string {
  if (total <= 1) return sensorValueColor(90);
  const t = Math.min(Math.max(index, 0), total - 1) / (total - 1);
  return sensorValueColor(t * 90);
}

/** Metrics drawn as a LineString rather than a harvester-width swath polygon. */
const isLineMetric = (metric: TTrackMetric) => metric === "speed";

/**
 * Resolve a feature's color the way the Tracks screen's paint expressions
 * would, and style it directly.
 *
 * Styling lives here rather than going through fieldsScreenUtils.updateFeatureStyle
 * because that helper is written for field and hex polygons: it forces a 0.5 px
 * stroke, which is right for swath segments (adjacent ones must blend into a
 * continuous ribbon) but renders the speed LineString as an invisible hairline.
 */
function styleFeature(
  feature: Feature<Geometry>,
  metric: TTrackMetric,
  colorMode: TTrackColorMode,
  deviceColor: string,
  dayColor: string
) {
  let color: string;
  if (colorMode === "device") {
    color = deviceColor;
  } else if (colorMode === "date") {
    color = dayColor;
  } else if (metric === "sensor") {
    color = sensorValueColor(Number(feature.get("sensor") ?? 0));
  } else if (metric === "speed") {
    color = String(feature.get("line-color") ?? "#888888");
  } else {
    color = String(feature.get("metricColor") ?? "#888888");
  }

  feature.set("deviceColor", deviceColor);
  feature.set("dayColor", dayColor);
  feature.setStyle(
    isLineMetric(metric)
      ? new Style({ stroke: new Stroke({ color, width: 3 }) })
      : new Style({
          fill: new Fill({ color }),
          stroke: new Stroke({ color, width: 0.5 }),
        })
  );
}

const optOptions = GEOJSON_OPTS;

/** One machine's points for one day — the unit geometry is built from.
 *
 *  Building per (day, machine) rather than per machine means each feature knows
 *  its date (so it can be coloured by it), the swath never bridges an overnight
 *  gap, and the points handed to the builders are always chronological without
 *  a cross-day sort. */
export type TTrackGroup = {
  /** The UTC day the points were fetched and cached under, or the focused
   *  local day when one is open. Features are coloured by the LOCAL day of
   *  their own time, which can differ from a UTC day. */
  day: string;
  device: TFieldTrackDevice;
};

export type TFieldTrackLayerInput = {
  groups: TTrackGroup[];
  metric: TTrackMetric;
  colorMode: TTrackColorMode;
  /** Both are owned by the caller so the swatch in the panel and the swath on
   *  the map are literally the same value. */
  colorForDevice: (deviceId: string) => string;
  /** Keyed by local (browser) day. */
  colorForDay: (day: string) => string;
  /** Color/speed display filter; omitted = draw everything. */
  filter?: TTrackFilter | null;
  /** A single day read by time of day: only fixes inside [from, to] are drawn,
   *  and Date colouring becomes time-of-pass colouring. */
  time?: TTrackTimeRender | null;
};

export type TFieldTrackLayerResult = {
  features: Array<Feature<Geometry>>;
};

export function buildFieldTrackFeatures({
  groups,
  metric,
  colorMode,
  colorForDevice,
  colorForDay,
  filter,
  time,
}: TFieldTrackLayerInput): TFieldTrackLayerResult {
  const format = new GeoJSON();
  const features: Array<Feature<Geometry>> = [];
  // colorForDay is a legend lookup; a season can be tens of thousands of
  // features over a few dozen days.
  const dayColors = new Map<string, string>();
  const colorOfDay = (d: string): string => {
    let c = dayColors.get(d);
    if (c == null) {
      c = colorForDay(d);
      dayColors.set(d, c);
    }
    return c;
  };
  for (const { day, device } of groups) {
    if (!device.tracks?.length) continue;
    // Per machine: the turn gate is each device's own analytics setting.
    const keep = trackKeepFor(filter, metric === "sensor", device.minTurnRadiusM);
    const response = new DeviceStatisticsResponse({
      tracks: device.tracks,
      minSatellitesCount: device.minSatellitesCount,
    });
    if (response.getLength() === 0) continue;

    const deviceColor = colorForDevice(device.deviceId);
    // The batch's width. A row carrying its own `widthM` (the width timeline
    // at that fix) is drawn at that instead — see pointWidths in the builders.
    const width = device.width || 6.7;
    const delay = (device.delay ?? 16000) / 1000;
    // Passing the window explicitly also skips the <100 m bbox guard in
    // sensorFeatures, which would otherwise blank a short pass entirely.
    const times = response.getTimeList();
    const first = time ? Math.max(times[0], time.from) : times[0];
    const last = time
      ? Math.min(times[response.getLength() - 1], time.to)
      : times[response.getLength() - 1] + 1;
    // This machine did nothing inside the selected start/stop window.
    if (first > last) continue;
    const from = new Date(first * 1000);
    const to = new Date(last * 1000);

    let collection;
    switch (metric) {
      case "speed":
        collection = speedFeatures(response, from, to, keep);
        break;
      case "potato":
        collection = potatoFeatures(response, width, delay, from, to, keep);
        break;
      case "not_potato":
        collection = notPotatoFeatures(response, width, delay, from, to, keep);
        break;
      case "size":
        collection = sizeFeatures(response, width, delay, from, to, keep);
        break;
      case "moisture":
        collection = moistureFeatures(response, width, delay, from, to, keep);
        break;
      default:
        collection = sensorFeatures(response, width, delay, from, to, false, undefined, keep);
    }

    const parsed = format.readFeatures(collection, optOptions) as Array<
      Feature<Geometry>
    >;
    parsed.forEach((feature, i) => {
      // Ids must be unique across every group — the source is shared.
      feature.setId(`${device.deviceId}-${day}-${metric}-${i}`);
      // A UTC day's evening can be the viewer's next (or previous) day.
      const t = feature.get("t");
      const localDay = typeof t === "number" ? localDayKey(t) : day;
      feature.set("day", localDay);
      styleFeature(
        feature,
        metric,
        colorMode,
        deviceColor,
        time && typeof t === "number" ? time.colorForTime(t) : colorOfDay(localDay)
      );
    });
    // A loop, not push(...parsed): JavaScriptCore caps a call at 65,536
    // arguments and one device-day can approach that.
    for (const f of parsed) features.push(f);
  }

  return { features };
}

/**
 * Clip a tracks layer to a field's boundary, on the canvas.
 *
 * Shared by both map panes deliberately. The alternative — clipping the points
 * server-side — misdraws: a swath would stop short of the boundary, and two
 * fixes either side of a headland turn taken outside it would be bridged into a
 * ribbon across ground that was never harvested. So the whole time window the
 * machine spent on the field is drawn, including the drive to and from it, and
 * masked here.
 *
 * It lives in one place because it did not, once: the compare pane had no clip
 * at all and drew the machine's road trips across the map.
 *
 * @param getGeometry resolved per frame — the selected field can change, and on
 *   the first frames it is not resolved at all.
 */
export function installTrackClip(
  layer: { on: (type: any, fn: (e: any) => void) => void },
  map: { getPixelFromCoordinate: (c: any) => number[] | null },
  getGeometry: () => any
): void {
  // Whether prerender actually pushed a canvas state this frame. Without this,
  // a prerender that bailed early (geometry not resolved yet) still got a
  // restore() from postrender — an underflowed state stack that resets the
  // transform OpenLayers set up, so the layer draws once and is blank after.
  let saved = false;

  layer.on("prerender", (event: any) => {
    saved = false;
    const geometry = getGeometry();
    const ctx: CanvasRenderingContext2D | undefined = event.context;
    if (!geometry || !ctx) return;

    const rings: number[][][] =
      geometry.getType() === "MultiPolygon"
        ? geometry.getPolygons().flatMap((p: any) => p.getCoordinates())
        : geometry.getCoordinates();
    if (!rings?.length) return;

    const pixelRatio = event.frameState?.pixelRatio ?? 1;
    const path = new Path2D();
    let usable = false;

    for (const ring of rings) {
      let started = false;
      for (const coord of ring) {
        const pixel = map.getPixelFromCoordinate(coord as any);
        // A vertex with no pixel would silently tear a hole in the clip path,
        // so abandon the ring rather than draw a wrong one.
        if (!pixel) {
          started = false;
          break;
        }
        const x = pixel[0] * pixelRatio;
        const y = pixel[1] * pixelRatio;
        if (!started) {
          path.moveTo(x, y);
          started = true;
        } else {
          path.lineTo(x, y);
        }
      }
      if (started) {
        path.closePath();
        usable = true;
      }
    }

    // Nothing clippable — draw unclipped rather than clip to an empty path,
    // which would hide the layer entirely.
    if (!usable) return;

    ctx.save();
    saved = true;
    // evenodd so interior rings (exclusion zones) stay masked out, matching how
    // the yield hexes treat them.
    ctx.clip(path, "evenodd");
  });

  layer.on("postrender", (event: any) => {
    if (!saved) return;
    saved = false;
    event.context?.restore();
  });
}
